import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/entities/user';
import { ADMIN_QUERY_KEYS } from '@/features/admin/api/query-keys';
import { WORKSPACE_SCHEDULES_KEY } from '../useOpportunitySchedule';
import { ToolContextDataService } from '@inference.engine/api-client/web';
import { inferenceHttp } from '@/lib/api/inference-client';
import { notificationService } from '@/lib/services/notification.service';

interface UseToolConfigScopesParams {
  toolId: string;
  agentId: string;
}

export function useToolConfigScopes({ toolId, agentId }: UseToolConfigScopesParams) {
  const { t } = useTranslation('admin');
  const organizationId = useAuth((state) => state.signedInUser.value?.organization.id ?? '');
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [...ADMIN_QUERY_KEYS.tools.configScopes(toolId), organizationId, agentId],
    queryFn: async () => {
      const { data } = await ToolContextDataService.findAllByToolId({
        client: inferenceHttp,
        path: { toolId },
        query: { organizationId, agentId },
      });
      return (
        data ?? {
          orgConfig: null,
          workspaceConfigs: [],
        }
      );
    },
    enabled: !!organizationId && !!toolId,
  });

  const invalidateAll = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: ADMIN_QUERY_KEYS.tools.configScopes(toolId),
    });
    void queryClient.invalidateQueries({
      queryKey: ADMIN_QUERY_KEYS.tools.agents,
    });
    void queryClient.invalidateQueries({
      queryKey: ADMIN_QUERY_KEYS.tools.toolDetail,
    });
    void queryClient.invalidateQueries({
      queryKey: ADMIN_QUERY_KEYS.tools.toolConfig,
    });
    // Saving a configuration is what creates the workspace's job row on the server, so its
    // schedule only exists from this point on.
    void queryClient.invalidateQueries({
      queryKey: WORKSPACE_SCHEDULES_KEY,
    });
  }, [queryClient, toolId]);

  const saveMutation = useMutation({
    mutationFn: async (params: { data: Record<string, unknown>; workspaceId?: string }) => {
      const baseItems = [
        {
          toolId,
          organizationId,
          workspaceId: params.workspaceId,
          agentId,
          data: params.data,
        },
      ];
      await ToolContextDataService.batchUpsert({
        client: inferenceHttp,
        body: { items: baseItems },
      });
    },
    onSuccess: () => {
      invalidateAll();
      notificationService.showSuccess(t('admin.useToolConfigScopes.configurationSaved'));
    },
    onError: () => {
      notificationService.showError(t('admin.useToolConfigScopes.failedToSaveConfiguration'));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (params: { workspaceId?: string }) => {
      await ToolContextDataService.deleteByToolId({
        client: inferenceHttp,
        path: { toolId },
        query: {
          organizationId,
          workspaceId: params.workspaceId,
          agentId,
        },
      });
    },
    onSuccess: () => {
      invalidateAll();
      notificationService.showSuccess(t('admin.useToolConfigScopes.configurationRemoved'));
    },
    onError: () => {
      notificationService.showError(t('admin.useToolConfigScopes.failedToRemoveConfiguration'));
    },
  });

  const bulkSaveMutation = useMutation({
    mutationFn: async (params: { workspaceIds: string[]; data: Record<string, unknown> }) => {
      const baseItems = params.workspaceIds.map((workspaceId) => ({
        toolId,
        organizationId,
        workspaceId,
        agentId,
        data: params.data,
      }));
      await ToolContextDataService.batchUpsert({
        client: inferenceHttp,
        body: { items: baseItems },
      });
    },
    onSuccess: () => {
      invalidateAll();
      notificationService.showSuccess(
        t('admin.useToolConfigScopes.configurationSavedToWorkspaces'),
      );
    },
    onError: () => {
      notificationService.showError(t('admin.useToolConfigScopes.failedToSaveConfigurationTo'));
    },
  });

  return {
    scopes: query.data,
    isLoading: query.isLoading,
    save: saveMutation.mutateAsync,
    isSaving: saveMutation.isPending,
    remove: deleteMutation.mutateAsync,
    isRemoving: deleteMutation.isPending,
    bulkSave: bulkSaveMutation.mutateAsync,
    isBulkSaving: bulkSaveMutation.isPending,
  };
}
